/**
 * Authentication utility functions for token management and validation
 */

// LocalStorage keys
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Store access token in localStorage
 * @param token - Access token to store
 */
export const setStoredToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store token:', error);
  }
};

/**
 * Get access token from localStorage
 * @returns Access token or null if not found
 */
export const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve token:', error);
    return null;
  }
};

/**
 * Store refresh token in localStorage
 * @param token - Refresh token to store
 */
export const setStoredRefreshToken = (token: string): void => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store refresh token:', error);
  }
};

/**
 * Get refresh token from localStorage
 * @returns Refresh token or null if not found
 */
export const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve refresh token:', error);
    return null;
  }
};

/**
 * Remove all stored tokens from localStorage
 */
export const removeStoredTokens = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove tokens:', error);
  }
};

/**
 * Decode JWT token payload
 * @param token - JWT token to decode
 * @returns Decoded payload or null if invalid
 */
export const decodeToken = (token: string): any | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param token - JWT token to check
 * @param bufferMs - Buffer time in milliseconds (default: 0)
 * @returns True if token is expired or will expire within buffer time
 */
export const isTokenExpired = (token: string, bufferMs: number = 0): boolean => {
  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) {
      return true;
    }

    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();

    return currentTime + bufferMs >= expirationTime;
  } catch (error) {
    console.error('Failed to check token expiration:', error);
    return true;
  }
};

/**
 * Get token expiration time
 * @param token - JWT token
 * @returns Expiration timestamp in milliseconds or null if invalid
 */
export const getTokenExpiration = (token: string): number | null => {
  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) {
      return null;
    }

    return decoded.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Failed to get token expiration:', error);
    return null;
  }
};

/**
 * Format authorization header value
 * @param token - Access token
 * @returns Formatted authorization header value (Bearer token)
 */
export const formatAuthHeader = (token: string): string => {
  return `Bearer ${token}`;
};

/**
 * Extract token from authorization header
 * @param header - Authorization header value
 * @returns Token or null if invalid format
 */
export const extractTokenFromHeader = (header: string): string | null => {
  try {
    const parts = header.split(' ');
    if (parts.length !== 2 || parts[0].toLowerCase() !== 'bearer') {
      return null;
    }
    return parts[1];
  } catch (error) {
    console.error('Failed to extract token from header:', error);
    return null;
  }
};

/**
 * Validate token format (basic check)
 * @param token - Token to validate
 * @returns True if token format is valid
 */
export const isValidTokenFormat = (token: string): boolean => {
  try {
    const parts = token.split('.');
    return parts.length === 3;
  } catch (error) {
    return false;
  }
};

/**
 * Get user ID from token
 * @param token - JWT token
 * @returns User ID or null if not found
 */
export const getUserIdFromToken = (token: string): string | null => {
  try {
    const decoded = decodeToken(token);
    return decoded?.sub || decoded?.user_id || null;
  } catch (error) {
    console.error('Failed to get user ID from token:', error);
    return null;
  }
};

/**
 * Get token claims
 * @param token - JWT token
 * @returns Token claims object or null if invalid
 */
export const getTokenClaims = (token: string): Record<string, any> | null => {
  try {
    return decodeToken(token);
  } catch (error) {
    console.error('Failed to get token claims:', error);
    return null;
  }
};

/**
 * Check if user has required role
 * @param token - JWT token
 * @param requiredRole - Required role to check
 * @returns True if user has the required role
 */
export const hasRole = (token: string, requiredRole: string): boolean => {
  try {
    const decoded = decodeToken(token);
    const roles = decoded?.roles || decoded?.role || [];
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(requiredRole);
  } catch (error) {
    console.error('Failed to check user role:', error);
    return false;
  }
};

export default {
  setStoredToken,
  getStoredToken,
  setStoredRefreshToken,
  getStoredRefreshToken,
  removeStoredTokens,
  decodeToken,
  isTokenExpired,
  getTokenExpiration,
  formatAuthHeader,
  extractTokenFromHeader,
  isValidTokenFormat,
  getUserIdFromToken,
  getTokenClaims,
  hasRole,
};
